import { IonButton, IonIcon } from '@ionic/react';
import { closeCircleOutline, linkOutline } from 'ionicons/icons';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { showError } from '../../../config/utils/formDiffUtils';
import { ProductInOrder } from '../../../interfaces/product.interface';
import { isCorrelationRequired } from '../../../services/utils/myDataUtils';
import { CorrelatedInvoicesModal } from './CorrelatedInvoicesModal';

interface Props {
  formik: any;
  customerId: string | null | undefined;
  initialUiMarks?: string[];
}

export const CorrelatedInvoicesSection = ({ formik, customerId, initialUiMarks }: Props) => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  // Track marks added via the picker UI (as opposed to typed manually).
  // Seeded with initialUiMarks so correlate-mode pre-fills show the dot too.
  const [uiAddedMarks, setUiAddedMarks] = useState<string[]>(initialUiMarks || []);

  const isRequired = useMemo(
    () => isCorrelationRequired(formik?.values?.documentTypeId?.object?.aadeCode),
    [formik?.values?.documentTypeId],
  );
  const arrayError =
    typeof formik.errors.correlatedInvoices === 'string' ? formik.errors.correlatedInvoices : undefined;
  const hasError = showError(formik, arrayError);

  const correlatedInvoices: { id: string | null; mark: string }[] = formik.values.correlatedInvoices || [];

  const handleRemove = useCallback(
    (mark: string) => {
      formik.setFieldValue(
        'correlatedInvoices',
        correlatedInvoices.filter((c) => c.mark !== mark),
      );
      setUiAddedMarks((prev) => prev.filter((m) => m !== mark));
    },
    [correlatedInvoices, formik],
  );

  const handleSelectInvoice = useCallback(
    (id: string, mark: string, products: ProductInOrder[]) => {
      if (!mark) return;

      const alreadyExists = correlatedInvoices.some((c) => c.mark === mark);
      const newCorrelated = alreadyExists ? correlatedInvoices : [...correlatedInvoices, { id, mark }];

      const current: any[] = formik.values.products || [];
      const isOnlyEmpty = current.length === 1 && !current[0]?.name && !current[0]?.description;
      const incoming = products.map((p: any) => ({ ...p, id: undefined }));
      const newProducts = alreadyExists ? current : isOnlyEmpty ? incoming : [...current, ...incoming];

      // Batch both field changes into one setValues call to avoid triggering
      // Formik's FieldArray componentDidUpdate → validateForm cycle twice,
      // which causes an infinite "Maximum update depth exceeded" loop.
      formik.setValues((prev: any) => ({ ...prev, correlatedInvoices: newCorrelated, products: newProducts }));

      if (!alreadyExists) {
        setUiAddedMarks((prev) => (prev.includes(mark) ? prev : [...prev, mark]));
      }

      setIsModalOpen(false);
    },
    [correlatedInvoices, formik],
  );

  const handleManualAdd = useCallback(
    (mark: string) => {
      if (!mark || correlatedInvoices.some((c) => c.mark === mark)) return;
      formik.setFieldValue('correlatedInvoices', [...correlatedInvoices, { id: null, mark }]);
      // Manual – no dot indicator
      setIsModalOpen(false);
    },
    [correlatedInvoices, formik],
  );

  const showErrorUI = useMemo(
    () => (isRequired && correlatedInvoices.length === 0) || hasError,
    [correlatedInvoices.length, hasError, isRequired],
  );

  if (!customerId) return null;

  return (
    <div className="width-100 margin-t-10">
      <div className="padding-b-16 padding-l-12 flex-space-between-center-row-wrap gap-8">
        <IonIcon icon={linkOutline} style={{ fontSize: 16 }} />
        <span style={{ flex: 1 }} className={showErrorUI ? 'text-danger' : ''}>
          {t('SALE.CORRELATED_INVOICES_TITLE')}
          {isRequired ? ' *' : ''}
        </span>
        <IonButton size="small" fill="outline" data-testid="add_correlated_btn" onClick={() => setIsModalOpen(true)}>
          <IonIcon icon={linkOutline} slot="start" />
          {t('SALE.ADD_CORRELATED')}
        </IonButton>
      </div>

      {correlatedInvoices.length === 0 && (
        <div style={{ fontSize: 12, opacity: 0.5, paddingLeft: 12, paddingBottom: 8 }}>
          {t('SALE.CORRELATED_INVOICES_EMPTY')}
        </div>
      )}

      {correlatedInvoices.map((item) => (
        <div
          key={item.mark}
          data-testid={`mark_row_${item.mark}`}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '6px 12px',
            marginBottom: 4,
            background: 'rgba(255,255,255,0.04)',
            borderRadius: 6,
          }}>
          {uiAddedMarks.includes(item.mark) && (
            <span
              data-testid={`mark_dot_${item.mark}`}
              title={t('SALE.CORRELATED_MARK_FROM_LIST')}
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: '#4f8ef7',
                flexShrink: 0,
                display: 'inline-block',
              }}
            />
          )}
          <span style={{ flex: 1, fontSize: 13, fontFamily: 'monospace' }}>{item.mark}</span>
          <span
            data-testid={`remove_mark_${item.mark}`}
            style={{ display: 'flex', cursor: 'pointer' }}
            onClick={() => handleRemove(item.mark)}>
            <IonIcon icon={closeCircleOutline} style={{ fontSize: 18, opacity: 0.6 }} />
          </span>
        </div>
      ))}

      <CorrelatedInvoicesModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        customerId={customerId}
        alreadyCorrelated={correlatedInvoices}
        onSelectInvoice={handleSelectInvoice}
        onManualAdd={handleManualAdd}
        currentDocAadeCode={formik?.values?.documentTypeId?.object?.aadeCode}
      />
    </div>
  );
};
